<!--

  v1.0
  Composant: b122-table

  Affichage des résultats
  - Limiter les éléments affichés (défaut : tous les éléments de la table en BDD)
  - Changer les en-têtes de colonnes (défaut : les en-têtes de colonnes correspondent aux propriétés de l'objet en BDD)
  - Formatter les valeurs affichées dans la ligne (défaut : les valeurs brutes de la BDD sont affichées pour chaque résultat)

  Tri
  - Changer le tri aprilyos (défaut : id desc)
  - Ajouter la possibilité de trier au niveau de l'en-tête de colonne (défaut: non actif)

  Actions
  - Afficher ou non l'oeil à gauche pour le show (défaut : caché)
  - Afficher ou non les actions edition et suppression (défaut : caché)
  - Possibilité de surcharger les boutons d'action (défaut : non actif)

  Pagination
  - Nombre de résultats par page utilisé par défaut (défaut : 50)
  - Options possibles pour le nombre de résultats par page (défaut : 0, 50, 100, Tout)

  Recherche
  - Propriétés de l'objet sur lesquelles se fait la recherche (défaut: id)

  Filtres
  - Possiblité d'ajouter des filtres pour limiter les résultats affichés par objet parent, par propriété... (défaut: non actif)

  Persistance de l'affichage
  - Les modifications d'affichage du tableau (tri, recherche, filtre) sont conservées tant que l'utilisateur est connecté.
  - Un bouton permet de réinitialiser l'affichage. (edited)
-->
<template>

  <div class="b122-table-container">

    <div v-if="useFilters" class="d-flex flex-column-reverse flex-lg-row justify-content-lg-between mb-2">

      <div class="d-lg-flex">
        <div v-for="filter in filters" class="mr-0 mr-lg-2 mb-2 mb-lg-0">
          <div v-if="filter.type === 'button'" class="row ml-2">
            <template v-for="button in filter.options">
              <b-button v-if="selectedButtonFilters.includes(button.value.id)" :id="'btn-filter-' + button.value.id"
                        :class="'mr-1 filter-button ' + button.value.cssClasses + '-selected'" variant="filter"
                        @click="doClickOnFilterButton(filter, button)">
                {{ $t(button.value.title) }}
              </b-button>
              <b-button v-else :id="'btn-filter-' + button.value.id"
                        :class="'mr-1 filter-button ' + button.value.cssClasses"
                        variant="filter"
                        @click="doClickOnFilterButton(filter, button)">
                {{ $t(button.value.title) }}
              </b-button>
            </template>
          </div>
          <vc-date-picker
            v-else-if="filter.type === 'datepicker'"
            v-model="filter.selectedValue"
            :first-day-of-week="2"
            :popover="{ visibility: 'click' }"
            class="d-flex flex-row"
            color="orange"
            is-range>
            <template v-slot="{ inputValue, inputEvents }">
              <input
                :class="{'active': filter.selectedValue}"
                :value="inputValue.start ? inputValue.start+' - '+inputValue.end : filter.options[0].text"
                class="border px-1 py-1 rounded datepicker-B122-table"
                v-on="inputEvents.start"/>
              <square-x-icon
                v-if="filter.selectedValue"
                class="datepicker-reset-icon"
                @click="filter.selectedValue = null">
              </square-x-icon>
              <input
                :value="inputValue.end"
                class=""
                hidden
                v-on="inputEvents.end"/>
            </template>
          </vc-date-picker>
          <b-form-select
            v-else
            v-model="filter.selectedValue"
            :class="{'active': filter.selectedValue}"
            class="filter-select">
            <option v-for="option in filter.options" :value="option.value">
              {{ $t(option.text) }}
            </option>
          </b-form-select>
        </div>
      </div>

      <div class="text-right mb-2 mb-lg-0">
        <b-link class="reset-link" @click="resetTable">
          {{ $t('ResetDisplay') }}
        </b-link>
      </div>

    </div>

    <div v-if="usePagination" class="d-flex flex-column flex-md-row justify-content-md-between mb-4">

      <b-input-group
        class="mr-md-4"
        size="sm">
        <b-input-group-prepend>
          <b-input-group-text class="search-input-prepend">
            <search-icon size="18"/>
          </b-input-group-text>
        </b-input-group-prepend>
        <b-form-input
          v-model="search"
          :class="{'active': search}"
          :placeholder="$t('Research')"
          class="search-input"
          type="search">
        </b-form-input>
      </b-input-group>

      <b122-table-pagination
        :currentPage="currentPage"
        :perPage="perPage"
        :perPageOptions="perPageOptions"
        :totalRows="totalRows"
        class="mt-2 mt-lg-0"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"/>
    </div>

    <div v-if="selectMode === 'multi' && selected.length > 0" ref="selectedActions" class="d-flex">

      <b-link class="select-all-link" @click="$emit('selectAllAction')">
        <send-icon class="main-color ml-1 mr-1" size="14"/>
        <span class="main-color-text">{{ selectAllActionTitle }}</span>
      </b-link>

      <b-link v-if="unselectAllActionTitle" class="ml-4 unselect-all-link" @click="$emit('unselectAllAction')">
        <div v-html="unselectAllActionTitle"></div>
      </b-link>
    </div>

    <div class="mb-4 overflow-auto table-container">

      <b-table v-if="selectMode == 'NONE'"
               ref="b122Table"
               :api-url="getB122TableResultsUrl"
               :busy="isBusy"
               :current-page="currentPage"
               :empty-text="emptyText"
               :empty-title="emptyTitle"
               :fields="fields"
               :items="provider"
               :per-page="perPage"
               :sort-by.sync="sortBy"
               :sort-desc.sync="sortDesc"
               :tbody-tr-class="tbodyTrClass"
               borderless
               show-empty
               sort-icon-left
               stacked="lg"
               striped>

        <template #table-busy>
          <div class="busy">
            <b-spinner style="width: 3em; height: 3em;"></b-spinner>
          </div>
        </template>

        <template #empty="scope">
          <div class="no-result">
            <div class="text-center mt-5 mb-4">
              <img :src="require(`@/assets/images/APRILYOS/empty-table.png`)" alt="no result" class="no-result-img"
                   width="90%">
            </div>
            <div class="no-result-text">
              <div class="no-result-title mb-2">{{ emptyTitle }}</div>
              <div class="main-color-text-text">{{ emptyText }}</div>
              <b-button v-if="createItem" class="my-2" variant="primary" @click="$emit('add')">
                {{ emptyButton }}
              </b-button>
            </div>
          </div>

        </template>

        <template v-if="!overrideShowCell" #cell(show)="row">
          <button class="action-btn" @click="$emit('clickOnShowBtn', row)">
            <eye-icon size="18"/>
          </button>
        </template>

        <template v-for="(_, slotName) of $scopedSlots" v-slot:[slotName]="scope">
          <slot :name="slotName" v-bind="scope"/>
        </template>

        <!-- Afficher actions par défaut si pas de surcharge -->
        <template v-if="!overrideActionsCell" #cell(actions)="row">
          <div class="actions-container text-right">
            <button v-if="editItem" class="action-btn edit mr-1" @click="$emit('clickOnEditBtn', row)">
              <pencil-icon size="18"/>
            </button>
            <button v-if="deleteItem" class="action-btn delete" @click="$emit('clickOnDeleteBtn', row)">
              <trash-icon size="18"/>
            </button>
          </div>
        </template>

      </b-table>

      <b-table v-if="selectMode != 'NONE'"
               ref="b122Table"
               :api-url="getB122TableResultsUrl"
               :busy="isBusy"
               :current-page="currentPage"
               :fields="fields"
               :items="provider"
               :per-page="perPage"
               :select-mode="selectMode"
               :sort-by.sync="sortBy"
               :sort-desc.sync="sortDesc"
               borderless
               empty-text="Aucun résultat"
               selectable
               show-empty
               sort-icon-left
               stacked="lg"
               striped
               @row-selected="onRowSelected">

        <template #table-busy>
          <div class="busy">
            <b-spinner style="width: 3em; height: 3em;"></b-spinner>
          </div>
        </template>

        <template #empty="scope">
          <div class="no-result">
            <div class="text-center mt-5 mb-4">
              <img :src="require(`@/assets/images/APRILYOS/empty-table.png`)" alt="no result" class="no-result-img"
                   width="90%">
            </div>
            <div class="no-result-text">
              <div class="no-result-title mb-2">{{ emptyTitle }}</div>
              <div class="main-color-text-text">{{ emptyText }}</div>
              <b-button v-if="createItem" class="my-2" variant="primary" @click="$emit('add')">
                {{ emptyButton }}
              </b-button>
            </div>
          </div>

        </template>

        <template v-if="!overrideShowCell" #cell(show)="row">
          <button class="action-btn" @click="$emit('clickOnShowBtn', row)">
            <eye-icon size="18"/>
          </button>
        </template>

        <template #head(selected)="data">
          <b-form-checkbox :checked="allSelected" :indeterminate="allIndeterminate" button-variant="secondary"
                           @change="toggleAllRows"/>
        </template>

        <template #cell(selected)="data">
          <template v-if="data.rowSelected">
            <b-form-checkbox checked="true" @change="unselectByCheckbox(data.index)"/>
          </template>
          <template v-else>
            <b-form-checkbox @change="selectByCheckbox(data.index)"/>
          </template>
        </template>

        <template v-for="(_, slotName) of $scopedSlots" v-slot:[slotName]="scope">
          <slot :name="slotName" v-bind="scope"/>
        </template>

        <!-- Afficher actions par défaut si pas de surcharge -->
        <template v-if="!overrideActionsCell" #cell(actions)="row">
          <div class="actions-container text-right">
            <button v-if="editItem" class="action-btn edit mr-1" @click="$emit('clickOnEditBtn', row)">
              <pencil-icon size="18"/>
            </button>
            <button v-if="deleteItem" class="action-btn delete" @click="$emit('clickOnDeleteBtn', row)">
              <trash-icon size="18"/>
            </button>
          </div>
        </template>

      </b-table>
    </div>

    <div v-if="usePagination" class="d-flex justify-content-end">
      <b122-table-pagination
        :currentPage="currentPage"
        :perPage="perPage"
        :perPageOptions="perPageOptions"
        :totalRows="totalRows"
        @changeCurrentPage="changeCurrentPage"
        @changePerPage="changePerPage"/>
    </div>

  </div>
</template>

<script>

import _ from 'lodash'
import api from '../../services/api'
import B122TablePagination from './B122TablePagination'
import {mapActions} from 'vuex'
import i18n from "../../i18n";
import { loadBrandCssTable } from "../../utils/theme";



export default {

  name: 'b122-table',

  components: {
    B122TablePagination
  },

  props: {


    identifier: {
      type: String,
      required: true
    },

    // Data

    initB122TableUrl: {
      type: String,
      required: true
    },

    getB122TableResultsUrl: {
      type: String,
      required: true
    },

    fields: {
      type: Array
    },

    items: {
      type: Array
    },

    selectable: {
      type: Boolean,
      default: false,
    },

    //Possibilité de passer en mode multiple
    selectMode: {
      type: String,
      default: "NONE"
    },

    selectAllActionTitle: {
      type: String,
      required: false
    },

    unselectAllActionTitle: {
      type: String,
      required: false
    },

    emptyTitle: {
      type: String,
      default: ''
    },

    emptyText: {
      type: String,
      default: i18n.t('table.empty.text')
    },

    emptyButton: {
      type: String,
      default: i18n.t('Add')
    },

    // Actions

    createItem: {
      type: Boolean,
      default: true
    },

    editItem: {
      type: Boolean,
      default: true
    },

    deleteItem: {
      type: Boolean,
      default: true
    },

    // Use filters

    useFilters: {
      type: Boolean,
      default: true
    },

    usePagination: {
      type: Boolean,
      default: true
    },

    // Search

    initialSearch: {
      type: String,
      default: null
    },

    // Sort

    initialSortBy: {
      type: String,
      default: 'id'
    },

    initialSortDesc: {
      type: Boolean,
      default: true
    },

    // Paginate

    initialCurrentPage: {
      type: Number,
      default: 1
    },

    initialPerPage: {
      type: Number,
      default: 50
    },
    tbodyTrClass: {
      type: String,
      required: false,
      default: ''
    },


    initialPerPageOptions: {
      type: Array,
      default() {
        return [
          {
            value: 10,
            text: '10 ' + this.$i18n.t('perPage')
          },
          {
            value: 50,
            text: '50 ' + this.$i18n.t('perPage')
          },
          {
            value: 100,
            text: '100 ' + this.$i18n.t('perPage')
          },
          {
            value: 2147483647, // Max integer value
            text: this.$i18n.t('seeEverything')
          }
        ]
      }
    }
  },

  emits: [
    'setTotalCount',
    'setFilters',
    'clickOnShowBtn',
    'clickOnEditBtn',
    'clickOnDeleteBtn',
    'add',
    'tableRefreshed',
    'onRowSelected',
    'selectAllAction',
    'unselectAllAction',
    'resetDisplay',
    'showAllLinks'
  ],

  data() {
    return {

      // Search
      search: this.initialSearch, // keep

      // Sort
      sortBy: this.initialSortBy, // keep
      sortDesc: this.initialSortDesc, // keep

      // Paginate
      currentPage: this.initialCurrentPage, // keep
      perPage: this.initialPerPage, // keep
      perPageOptions: this.initialPerPageOptions,

      // Others
      filters: [], // keep
      debouncedRefreshTable: null,
      totalRows: 0,
      isBusy: false,
      selectedButtonFilters: [],
      selected: [],
      allSelected: false,
      allIndeterminate: false
    }
  },

  computed: {

    overrideShowCell() {
      return this.$scopedSlots['cell(show)']
    },

    overrideActionsCell() {
      return this.$scopedSlots['cell(actions)']
    }

  },

  watch: {

    'search': function (newValue, oldValue) {
      this.debouncedRefreshTable()
    },

    'filters': {
      handler: function (newValue, oldValue) {
        this.debouncedRefreshTable()
      },
      deep: true
    }
  },

  created() {
    const brandKey = this.$store.getters['brand/brand'].key
    // loadBrandCssTable(brandKey)
    this.debouncedRefreshTable = _.throttle(this.refreshTable, 500)
    this.restoreStateFromStore()
  },

  mounted() {
    if (typeof (this.items) === 'undefined') {
      this.initB122Table()
    }
  },

  beforeDestroy() {
    this.saveState()
  },

  methods: {

    ...mapActions(['persistTableState']),

    changeCurrentPage(newCurrentPage) {
      this.currentPage = newCurrentPage
    },

    changePerPage(newPerPage) {
      this.perPage = newPerPage
      this.currentPage = 1
    },

    initB122Table() {
      api
        .get(this.initB122TableUrl)
        .then(response => {
          this.$emit('setTotalCount', response.data.totalCount)

          this.filters = response.data.filters

          const tableState = this.$store.getters.getStateForTable(this.identifier)
          if (tableState) {
            this.filters.map(filter => {
              const filterFromState = tableState.filters.filter(item => item.position === filter.position)
              if (filterFromState.length > 0) {
                filter.selectedValue = filterFromState[0].selectedValue ? filterFromState[0].selectedValue : ''
              }
            })
          }
          this.$emit('setFilters', this.filters)
        })
    },

    provider(ctx) {
      let formData = new FormData()
      formData.append('perPage', ctx.perPage)
      formData.append('currentPage', ctx.currentPage)
      formData.append('sortBy', ctx.sortBy)
      formData.append('sortDesc', ctx.sortDesc)

      if (this.search && this.search.trim() !== '') {
        formData.append('search', this.search.trim())
      }

      const filtersFormatted = this.filters.map((filter) => {
        return {
          'position': filter.position,
          'selectedValue': filter.selectedValue
        }
      })
      formData.append('filters', JSON.stringify(filtersFormatted))

      const promise = api({
        method: 'POST',
        url: ctx.apiUrl,
        data: formData
      })
      return promise.then((response) => {
        const items = response.data.items
        this.totalRows = response.data.totalRows
        this.$emit('setTotalCount', response.data.totalRows)
        return items || []
      })
    },

    refreshTable() {
      this.$refs.b122Table.refresh()
      this.$emit('tableRefreshed')
    },

    resetTable() {
      this.currentPage = 1
      this.perPage = this.initialPerPage
      this.search = this.initialSearch
      this.filters.map(filter => filter.selectedValue = '')
      this.sortBy = this.initialSortBy
      this.sortDesc = this.initialSortDesc
      this.selectedButtonFilters = []
      this.refreshTable()
      this.$emit('resetDisplay')
    },

    saveState() {
      this.persistTableState({
        identifier: this.identifier,
        search: this.search,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        totalRows: this.totalRows,
        currentPage: this.currentPage,
        perPage: this.perPage,
        filters: this.filters.map(item => ({position: item.position, selectedValue: item.selectedValue}))
      })
    },

    restoreStateFromStore() {
      const tableState = this.$store.getters.getStateForTable(this.identifier)
      if (!tableState) {
        return
      }
      this.search = tableState.search
      this.sortBy = tableState.sortBy
      this.sortDesc = tableState.sortDesc
      this.totalRows = tableState.totalRows
      this.perPage = tableState.perPage
      this.currentPage = tableState.currentPage
    },

    doClickOnFilterButton(filter, button) {
      if (this.selectedButtonFilters.includes(button.value.id)) {
        this.selectedButtonFilters.splice(this.selectedButtonFilters.indexOf(button.value.id), 1);
      } else {
        this.selectedButtonFilters.push(button.value.id)
      }
      filter.selectedValue = this.selectedButtonFilters.toString()
    },

    unselectByCheckbox(index) {
      this.$refs.b122Table.unselectRow(index)
    },

    selectByCheckbox(index) {
      this.$refs.b122Table.selectRow(index)
    },

    onRowSelected(items) {
      this.$emit('onRowSelected', items)
      this.selected = items
      this.allSelected = this.selected.length === this.totalRows;
      this.allIndeterminate = !this.allSelected && this.selected.length > 0;
    },

    toggleAllRows() {
      if (this.allSelected) {
        this.$refs.b122Table.clearSelected()
      } else {
        this.$refs.b122Table.selectAllRows()
      }
    },
  }
}
</script>




// WEBPACK FOOTER //
// src/components/b122/B122Table.vue